import { trace } from "@opentelemetry/api";
import type { NextPage } from "next";
import { SelectOption } from "@components/Features/MainPage/MainFilter/types";
import CatalogScreen from "@components/Screens/CatalogScreen/CatalogScreen";
import {
    getBrandsOptions,
    getCityOptions,
    getGenerationsOptions,
    getModelsOptions
} from "@app/store/filtersOptionsApi";
import { setBrand, setCities, setCountry, setGeneration, setModel } from "@app/store/filtersSlice";
import { getOffers, QueryOffersParams } from "@app/store/offersApi";
import { wrapper } from "@app/store/store";
import Template from "@app/Template/Template";
import { parseQueryParams } from "@entities/catalog/lib";
import { parseCatalogSlug } from "@entities/catalog/lib/parseCatalogSlug";
import Car from "@shared/lib/interfaces/car.interface";
import { RouterSlugs } from "@shared/lib/routerSlugs";

export interface CatalogPageProps {
    preloadOffers?: Car[];
    preloadQuantity?: number;
    preloadTotalPages?: number;
}

const Catalog: NextPage = ({
    preloadOffers,
    preloadQuantity,
    preloadTotalPages
}: CatalogPageProps) => {
    return (
        <Template>
            <CatalogScreen
                preloadedProps={{
                    preloadOffers,
                    preloadQuantity,
                    preloadTotalPages
                }}
            />
        </Template>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async (context) => {
    const tracer = trace.getTracer("getServerSideProps /catalog/[[...slug]]");

    let slug = context.params?.slug;
    if (Array.isArray(slug)) {
        if (slug[slug.length - 1].toLowerCase() === RouterSlugs.SLUG_FILTERS_END) {
            slug = slug.slice(0, -1);
        }
    }

    const { country, citySlug, brandSlug, modelSlug, generation, restyling } = parseCatalogSlug(
        Array.isArray(slug) ? slug : []
    );

    const cityAlias = citySlug?.toLowerCase();
    const brandAlias = brandSlug?.toLowerCase();
    const modelAlias = modelSlug?.toLowerCase();
    const { bodyType, priceFrom, priceTo, page } = parseQueryParams(context.query, store);

    const cityValue = cityAlias && cityAlias !== "all-cities" ? cityAlias : undefined;

    const filters: QueryOffersParams = {
        page,
        ...(country ? { country } : {}),
        ...(cityValue ? { cityAlias: cityValue } : {}),
        ...(brandAlias ? { brandAlias } : {}),
        ...(modelAlias ? { modelAlias } : {}),
        ...(generation ? { generation } : {}),
        ...(restyling ? { restyling } : {}),
        ...(bodyType ? { body_type: bodyType } : {}),
        ...(priceFrom ? { from_price: priceFrom } : {}),
        ...(priceTo ? { to_price: priceTo } : {})
    };

    try {
        const getOffersSpan = tracer.startSpan("getOffers");
        const traceId = getOffersSpan.spanContext().traceId;
        const { data } = await store.dispatch(getOffers.initiate({ ...filters, traceId }));
        getOffersSpan.end();

        if (country) {
            store.dispatch(setCountry(country));
        }

        if (cityValue) {
            const getCitiesSpan = tracer.startSpan("getCities");
            const { data: cityOptions } = await store.dispatch(
                getCityOptions.initiate({
                    exact: true,
                    cityName: cityValue
                })
            );
            getCitiesSpan.end();

            const foundCity = cityOptions?.[0];
            if (foundCity) {
                store.dispatch(setCities([foundCity]));
                filters.cityAlias = foundCity.alias;
            }
        }

        if (brandAlias) {
            const getBrandsSpan = tracer.startSpan("getBrands");
            const { data: brandsOptions } = await store.dispatch(getBrandsOptions.initiate({}));
            getBrandsSpan.end();
            const selectedBrand = brandsOptions.find(
                (brand: SelectOption) => brand.alias === brandAlias
            );
            if (selectedBrand) {
                store.dispatch(setBrand(selectedBrand));
                filters.brandUuid = selectedBrand.id;
            }

            if (selectedBrand && modelAlias) {
                const getModelsSpan = tracer.startSpan("getModels");
                const { data: modelsOptions } = await store.dispatch(
                    getModelsOptions.initiate({ brandUuid: selectedBrand?.id })
                );
                getModelsSpan.end();
                const selectedModel = modelsOptions.find(
                    (model: SelectOption) => model.alias === modelAlias
                );
                if (selectedModel) {
                    store.dispatch(setModel(selectedModel));
                    filters.modelUuid = selectedModel.id;

                    if (generation) {
                        const getGenerationsSpan = tracer.startSpan("getGenerations");
                        const { data: generationsOptions } = await store.dispatch(
                            getGenerationsOptions.initiate({
                                model_uuid: selectedModel.id
                            })
                        );
                        getGenerationsSpan.end();
                        const selectedGeneration = generationsOptions.find(
                            (gen: { generation: number; restyleNumber: number }) =>
                                gen.generation === generation &&
                                gen.restyleNumber === (restyling || 0)
                        );
                        if (selectedGeneration) {
                            store.dispatch(setGeneration(selectedGeneration));
                            filters.generationUuid = selectedGeneration.id;
                        }
                    }
                }
            }
        }

        return {
            props: {
                preloadOffers: data.offers,
                preloadQuantity: data.meta.total,
                preloadTotalPages: data.meta.totalPage
            }
        };
    } catch (error) {
        console.error(error);
        return { notFound: true };
    }
});

export default Catalog;